import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Copy, DollarSign, Link2, Settings, TrendingUp, Users, Wallet } from 'lucide-react';
import { API, toast } from '@/App';
import SearchBar from '@/components/shared/SearchBar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  formatAffiliateCurrency,
  formatAffiliateDateTime,
  getAffiliateStatusLabel,
  getAffiliateStatusToneClasses,
  getPayoutStatusLabel,
  getPayoutStatusVariant,
} from '@/utils/affiliateWorkspace';

const DEFAULT_PROGRAM_SETTINGS = {
  is_enabled: false,
  commission_rate: 10,
  referral_discount_percent: 20,
  ambassador_percent: 50,
  super_ambassador_direct_percent: 50,
  super_ambassador_indirect_percent: 25,
  min_payout_amount: 500,
  payment_method: 'bank_transfer',
  auto_approve: false,
};

const DEFAULT_SETTINGS_META = {
  sectionTitle: 'Revenue Sharing & Referral Settings',
  calculationNote: '',
  warnings: [],
  examplePreview: null,
};

function normalizeProgramSettingsPayload(payload = {}) {
  return {
    ...DEFAULT_PROGRAM_SETTINGS,
    is_enabled: Boolean(payload.is_enabled),
    commission_rate: Number(payload.commission_rate ?? DEFAULT_PROGRAM_SETTINGS.commission_rate),
    referral_discount_percent: Number(payload.referral_discount_percent ?? DEFAULT_PROGRAM_SETTINGS.referral_discount_percent),
    ambassador_percent: Number(payload.ambassador_percent ?? DEFAULT_PROGRAM_SETTINGS.ambassador_percent),
    super_ambassador_direct_percent: Number(payload.super_ambassador_direct_percent ?? DEFAULT_PROGRAM_SETTINGS.super_ambassador_direct_percent),
    super_ambassador_indirect_percent: Number(payload.super_ambassador_indirect_percent ?? DEFAULT_PROGRAM_SETTINGS.super_ambassador_indirect_percent),
    min_payout_amount: Number(payload.min_payout_amount ?? DEFAULT_PROGRAM_SETTINGS.min_payout_amount),
    payment_method: payload.payment_method || DEFAULT_PROGRAM_SETTINGS.payment_method,
    auto_approve: Boolean(payload.auto_approve),
  };
}

function extractProgramSettingsMeta(payload = {}) {
  return {
    sectionTitle: payload.section_title || DEFAULT_SETTINGS_META.sectionTitle,
    calculationNote: payload.calculation_note || '',
    warnings: Array.isArray(payload.warnings) ? payload.warnings : [],
    examplePreview: payload.example_preview || null,
  };
}

const AffiliateManagement = () => {
  const [affiliates, setAffiliates] = useState([]);
  const [payoutRequests, setPayoutRequests] = useState([]);
  const [programSettings, setProgramSettings] = useState(DEFAULT_PROGRAM_SETTINGS);
  const [settingsMeta, setSettingsMeta] = useState(DEFAULT_SETTINGS_META);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);

  const loadData = async () => {
    setLoading(true);
    try {
      const [affiliatesResponse, payoutsResponse, settingsResponse] = await Promise.all([
        axios.get(`${API}/admin/affiliates`, { params: { affiliate_type: 'seller' } }),
        axios.get(`${API}/admin/affiliates/payouts`, { params: { affiliate_type: 'seller' } }),
        axios.get(`${API}/admin/affiliate-settings`),
      ]);
      setAffiliates(Array.isArray(affiliatesResponse.data) ? affiliatesResponse.data : []);
      setPayoutRequests(Array.isArray(payoutsResponse.data) ? payoutsResponse.data : []);
      setProgramSettings(normalizeProgramSettingsPayload(settingsResponse.data || {}));
      setSettingsMeta(extractProgramSettingsMeta(settingsResponse.data || {}));
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Erreur lors du chargement des affiliations vendeur.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const filteredAffiliates = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) {
      return affiliates;
    }
    return affiliates.filter((affiliate) => {
      const haystack = [
        affiliate.user_name,
        affiliate.user_email,
        affiliate.affiliate_code,
      ]
        .map((value) => String(value || '').toLowerCase())
        .join(' ');
      return haystack.includes(query);
    });
  }, [affiliates, searchQuery]);

  const summary = useMemo(() => {
    return {
      active: affiliates.filter((item) => item.status_code === 'active').length,
      clicks: affiliates.reduce((sum, item) => sum + Number(item.total_clicks || 0), 0),
      registrations: affiliates.reduce((sum, item) => sum + Number(item.total_referrals || 0), 0),
      commissions: affiliates.reduce((sum, item) => sum + Number(item.total_earnings || 0), 0),
      available: affiliates.reduce((sum, item) => sum + Number(item.available_amount || 0), 0),
    };
  }, [affiliates]);

  const pendingPayouts = payoutRequests.filter((item) => String(item.status || '').toLowerCase() === 'pending').length;
  const examplePreview = settingsMeta.examplePreview;
  const settingsWarnings = Array.isArray(settingsMeta.warnings) ? settingsMeta.warnings : [];

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      const response = await axios.patch(`${API}/admin/affiliate-settings`, programSettings);
      const responseSettings = response?.data?.settings;
      if (responseSettings) {
        setProgramSettings(normalizeProgramSettingsPayload(responseSettings));
        setSettingsMeta(extractProgramSettingsMeta(responseSettings));
      }
      const changedFields = Array.isArray(response?.data?.changed_fields) ? response.data.changed_fields : [];
      toast.success(changedFields.length > 0 ? 'Configuration revenue sharing enregistree.' : 'Aucune modification detectee.');
      setShowSettingsDialog(false);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Erreur lors de l'enregistrement.");
    } finally {
      setSavingSettings(false);
    }
  };

  const handleToggleAffiliateStatus = async (affiliateId, currentStatus) => {
    try {
      await axios.patch(`${API}/admin/affiliates/${affiliateId}`, { is_active: !currentStatus });
      toast.success(currentStatus ? 'Affilie vendeur suspendu.' : 'Affilie vendeur reactive.');
      loadData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Erreur lors de la mise a jour.');
    }
  };

  const handleProcessPayout = async (payoutId, action) => {
    try {
      await axios.patch(`${API}/admin/affiliates/payouts/${payoutId}`, { status: action });
      toast.success(
        action === 'approved'
          ? 'Paiement approuve.'
          : action === 'paid'
            ? 'Paiement marque comme paye.'
            : 'Paiement rejete.'
      );
      loadData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Erreur lors du traitement.');
    }
  };

  if (loading) {
    return <div className="flex h-64 items-center justify-center text-sm text-slate-500">Chargement affiliation vendeur...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <h2 className="text-3xl font-bold text-slate-900">Affilies vendeurs</h2>
          <p className="mt-1 text-sm text-slate-500">
            Gestion admin separee des vendeurs affilies, de leurs performances et de leurs commissions.
          </p>
        </div>
        <Button onClick={() => setShowSettingsDialog(true)}>
          <Settings className="mr-2 h-4 w-4" />
          Parametres du programme
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
        <Card><CardContent className="pt-6 text-center"><Users className="mx-auto mb-2 h-8 w-8 text-sky-600" /><p className="text-2xl font-bold">{summary.active}</p><p className="text-sm text-slate-500">Affilies actifs</p></CardContent></Card>
        <Card><CardContent className="pt-6 text-center"><Link2 className="mx-auto mb-2 h-8 w-8 text-violet-600" /><p className="text-2xl font-bold">{summary.clicks.toLocaleString('fr-FR')}</p><p className="text-sm text-slate-500">Clics</p></CardContent></Card>
        <Card><CardContent className="pt-6 text-center"><TrendingUp className="mx-auto mb-2 h-8 w-8 text-cyan-600" /><p className="text-2xl font-bold">{summary.registrations.toLocaleString('fr-FR')}</p><p className="text-sm text-slate-500">Inscriptions</p></CardContent></Card>
        <Card><CardContent className="pt-6 text-center"><DollarSign className="mx-auto mb-2 h-8 w-8 text-emerald-600" /><p className="text-2xl font-bold">{formatAffiliateCurrency(summary.commissions)}</p><p className="text-sm text-slate-500">Commissions totales</p></CardContent></Card>
        <Card><CardContent className="pt-6 text-center"><Wallet className="mx-auto mb-2 h-8 w-8 text-amber-600" /><p className="text-2xl font-bold">{formatAffiliateCurrency(summary.available)}</p><p className="text-sm text-slate-500">Disponibles</p></CardContent></Card>
      </div>

      <Card className="border-slate-200">
        <CardHeader className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div>
            <CardTitle>{settingsMeta.sectionTitle}</CardTitle>
            <CardDescription>
              {settingsMeta.calculationNote || 'Les commissions sont calculees sur le montant net confirme par le backend.'}
            </CardDescription>
          </div>
          <Button onClick={() => setShowSettingsDialog(true)}>
            <Settings className="mr-2 h-4 w-4" />
            Modifier la configuration
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Reduction vendeur parraine</p>
              <p className="mt-2 text-2xl font-bold text-slate-900">{Number(programSettings.referral_discount_percent || 0)}%</p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Commission ambassadeur</p>
              <p className="mt-2 text-2xl font-bold text-slate-900">{Number(programSettings.ambassador_percent || 0)}%</p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Super ambassadeur direct</p>
              <p className="mt-2 text-2xl font-bold text-slate-900">{Number(programSettings.super_ambassador_direct_percent || 0)}%</p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Super ambassadeur indirect</p>
              <p className="mt-2 text-2xl font-bold text-slate-900">{Number(programSettings.super_ambassador_indirect_percent || 0)}%</p>
            </div>
          </div>

          {examplePreview ? (
            <div className="rounded-2xl border border-slate-200 bg-white p-4">
              <div className="flex flex-col gap-1 md:flex-row md:items-center md:justify-between">
                <div>
                  <p className="text-sm font-semibold text-slate-900">Exemple de lecture admin</p>
                  <p className="text-xs text-slate-500">{examplePreview.note}</p>
                </div>
                <Badge variant="outline">Base exemple: {formatAffiliateCurrency(examplePreview.gross_amount, examplePreview.currency)}</Badge>
              </div>
              <div className="mt-4 grid gap-4 lg:grid-cols-3">
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-sm font-medium text-slate-900">Reduction vendeur</p>
                  <p className="mt-2 text-sm text-slate-600">
                    {formatAffiliateCurrency(examplePreview.referral_discount_amount, examplePreview.currency)} ({Number(examplePreview.referral_discount_percent || 0)}%)
                  </p>
                  <p className="mt-1 text-xs text-slate-500">Net apres reduction: {formatAffiliateCurrency(examplePreview.net_after_referral, examplePreview.currency)}</p>
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-sm font-medium text-slate-900">Cas ambassadeur</p>
                  <p className="mt-2 text-sm text-slate-600">
                    Commission: {formatAffiliateCurrency(examplePreview.ambassador_case?.commission_amount, examplePreview.currency)}
                  </p>
                  <p className="mt-1 text-xs text-slate-500">Taux: {Number(examplePreview.ambassador_case?.commission_percent || 0)}% du net</p>
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-sm font-medium text-slate-900">Cas super ambassadeur</p>
                  <p className="mt-2 text-sm text-slate-600">
                    Direct: {formatAffiliateCurrency(examplePreview.super_direct_case?.commission_amount, examplePreview.currency)}
                  </p>
                  <p className="mt-1 text-xs text-slate-500">
                    Indirect: {formatAffiliateCurrency(examplePreview.super_indirect_case?.super_amount, examplePreview.currency)} + ambassadeur {formatAffiliateCurrency(examplePreview.super_indirect_case?.ambassador_amount, examplePreview.currency)}
                  </p>
                </div>
              </div>
            </div>
          ) : null}

          {settingsWarnings.length > 0 ? (
            <div className="rounded-2xl border border-amber-300 bg-amber-50 p-4">
              <p className="text-sm font-semibold text-amber-900">Points de vigilance</p>
              <div className="mt-2 space-y-2 text-sm text-amber-800">
                {settingsWarnings.map((warning) => (
                  <p key={warning.code || warning.message}>{warning.message}</p>
                ))}
              </div>
            </div>
          ) : null}
        </CardContent>
      </Card>

      <Tabs defaultValue="affiliates" className="space-y-6">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="affiliates">Affilies vendeurs ({affiliates.length})</TabsTrigger>
          <TabsTrigger value="payouts">Paiements {pendingPayouts > 0 ? `(${pendingPayouts})` : ''}</TabsTrigger>
        </TabsList>

        <TabsContent value="affiliates">
          <Card>
            <CardHeader>
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div>
                  <CardTitle>Performance vendeurs</CardTitle>
                  <CardDescription>Les vendeurs affilies restent separes du pilotage ambassadeur.</CardDescription>
                </div>
                <SearchBar
                  value={searchQuery}
                  onChange={setSearchQuery}
                  placeholder="Rechercher un affilie vendeur..."
                  className="max-w-xs"
                />
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Vendeur</TableHead>
                    <TableHead>Code</TableHead>
                    <TableHead>Clics</TableHead>
                    <TableHead>Inscriptions</TableHead>
                    <TableHead>Conversions</TableHead>
                    <TableHead>Gains</TableHead>
                    <TableHead>Disponible</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAffiliates.map((affiliate) => (
                    <TableRow key={affiliate.id}>
                      <TableCell>
                        <div className="font-medium">{affiliate.user_name || '-'}</div>
                        <div className="text-xs text-slate-500">{affiliate.user_email || '-'}</div>
                        <div className="text-xs text-slate-400">{formatAffiliateDateTime(affiliate.last_registration_at)}</div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <code className="rounded bg-slate-100 px-2 py-1 text-xs">{affiliate.affiliate_code}</code>
                          {affiliate.referral_link ? (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={async () => {
                                try {
                                  await navigator.clipboard.writeText(affiliate.referral_link);
                                  toast.success('Lien copie.');
                                } catch (error) {
                                  toast.error('Impossible de copier le lien.');
                                }
                              }}
                            >
                              <Copy className="h-3 w-3" />
                            </Button>
                          ) : null}
                        </div>
                      </TableCell>
                      <TableCell>{Number(affiliate.total_clicks || 0).toLocaleString('fr-FR')}</TableCell>
                      <TableCell>{Number(affiliate.total_referrals || 0).toLocaleString('fr-FR')}</TableCell>
                      <TableCell>{Number(affiliate.total_conversions || 0).toLocaleString('fr-FR')}</TableCell>
                      <TableCell>{formatAffiliateCurrency(affiliate.total_earnings || 0)}</TableCell>
                      <TableCell>{formatAffiliateCurrency(affiliate.available_amount || 0)}</TableCell>
                      <TableCell>
                        <span className={`inline-flex rounded-full border px-3 py-1 text-xs font-medium ${getAffiliateStatusToneClasses(affiliate.status_code)}`}>
                          {getAffiliateStatusLabel(affiliate.status_code)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Switch
                          checked={Boolean(affiliate.is_active)}
                          onCheckedChange={() => handleToggleAffiliateStatus(affiliate.id, affiliate.is_active)}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {filteredAffiliates.length === 0 && (
                <div className="py-8 text-center text-sm text-slate-500">Aucun affilie vendeur pour les filtres actuels.</div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="payouts">
          <Card>
            <CardHeader>
              <CardTitle>Demandes de paiement vendeur</CardTitle>
              <CardDescription>Historique et traitement des retraits des vendeurs affilies.</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Affilie</TableHead>
                    <TableHead>Montant</TableHead>
                    <TableHead>Methode</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payoutRequests.map((payout) => (
                    <TableRow key={payout.id}>
                      <TableCell>
                        <div className="font-medium">{payout.affiliate_name || '-'}</div>
                        <div className="text-xs text-slate-500">{payout.affiliate_code || '-'}</div>
                      </TableCell>
                      <TableCell>{formatAffiliateCurrency(payout.amount, payout.currency || 'FCFA')}</TableCell>
                      <TableCell>{payout.payment_method || '-'}</TableCell>
                      <TableCell>{formatAffiliateDateTime(payout.request_date)}</TableCell>
                      <TableCell>
                        <Badge variant={getPayoutStatusVariant(payout.status)}>{getPayoutStatusLabel(payout.status)}</Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-2">
                          <Button size="sm" variant="outline" onClick={() => handleProcessPayout(payout.id, 'approved')}>Approuver</Button>
                          <Button size="sm" variant="outline" onClick={() => handleProcessPayout(payout.id, 'paid')}>Payer</Button>
                          <Button size="sm" variant="destructive" onClick={() => handleProcessPayout(payout.id, 'rejected')}>Rejeter</Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {payoutRequests.length === 0 && (
                <div className="py-8 text-center text-sm text-slate-500">Aucune demande de paiement vendeur.</div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={showSettingsDialog} onOpenChange={setShowSettingsDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{settingsMeta.sectionTitle}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-sm font-semibold text-slate-900">Revenue Sharing & Referral Settings</p>
              <p className="mt-1 text-xs text-slate-500">
                Les commissions sont calculees sur le net confirme par le backend. Les codes promo compatibles peuvent reduire ce net avant commission.
              </p>
              <div className="mt-4 grid gap-4 md:grid-cols-2">
                <label className="block text-sm">
                  <span className="mb-1 block font-medium">Reduction vendeur parraine (%)</span>
                  <input className="h-10 w-full rounded-md border px-3" type="number" min="0" max="100" value={programSettings.referral_discount_percent || 0} onChange={(event) => setProgramSettings((current) => ({ ...current, referral_discount_percent: Number(event.target.value || 0) }))} />
                </label>
                <label className="block text-sm">
                  <span className="mb-1 block font-medium">Commission ambassadeur (%)</span>
                  <input className="h-10 w-full rounded-md border px-3" type="number" min="0" max="100" value={programSettings.ambassador_percent || 0} onChange={(event) => setProgramSettings((current) => ({ ...current, ambassador_percent: Number(event.target.value || 0) }))} />
                </label>
                <label className="block text-sm">
                  <span className="mb-1 block font-medium">Commission super ambassadeur direct (%)</span>
                  <input className="h-10 w-full rounded-md border px-3" type="number" min="0" max="100" value={programSettings.super_ambassador_direct_percent || 0} onChange={(event) => setProgramSettings((current) => ({ ...current, super_ambassador_direct_percent: Number(event.target.value || 0) }))} />
                </label>
                <label className="block text-sm">
                  <span className="mb-1 block font-medium">Commission super ambassadeur indirect (%)</span>
                  <input className="h-10 w-full rounded-md border px-3" type="number" min="0" max="100" value={programSettings.super_ambassador_indirect_percent || 0} onChange={(event) => setProgramSettings((current) => ({ ...current, super_ambassador_indirect_percent: Number(event.target.value || 0) }))} />
                </label>
              </div>
            </div>

            {settingsWarnings.length > 0 ? (
              <div className="rounded-2xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-800">
                {settingsWarnings.map((warning) => (
                  <p key={warning.code || warning.message}>{warning.message}</p>
                ))}
              </div>
            ) : null}

            <div className="rounded-2xl border border-slate-200 p-4">
              <p className="text-sm font-semibold text-slate-900">Parametres generaux du programme</p>
              <div className="mt-4 space-y-4">
                <label className="block text-sm">
                  <span className="mb-1 block font-medium">Programme actif</span>
                  <Switch checked={Boolean(programSettings.is_enabled)} onCheckedChange={(checked) => setProgramSettings((current) => ({ ...current, is_enabled: checked }))} />
                </label>
                <label className="block text-sm">
                  <span className="mb-1 block font-medium">Commission affiliation vendeur (%)</span>
                  <input className="h-10 w-full rounded-md border px-3" type="number" min="0" max="100" value={programSettings.commission_rate || 0} onChange={(event) => setProgramSettings((current) => ({ ...current, commission_rate: Number(event.target.value || 0) }))} />
                </label>
                <label className="block text-sm">
                  <span className="mb-1 block font-medium">Minimum retrait</span>
                  <input className="h-10 w-full rounded-md border px-3" type="number" min="0" value={programSettings.min_payout_amount || 0} onChange={(event) => setProgramSettings((current) => ({ ...current, min_payout_amount: Number(event.target.value || 0) }))} />
                </label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSettingsDialog(false)}>Annuler</Button>
            <Button onClick={handleSaveSettings} disabled={savingSettings}>{savingSettings ? 'Enregistrement...' : 'Enregistrer'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AffiliateManagement;
